if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput = this.form.querySelector('[name=id]');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
       
        // Store all variants data for later use
        this.variantsData = this.getVariantsData();
       
        // Initial price check for the default variant
        this.currentVariant = this.getSelectedVariant();
        this.setAvailability();

        // Set up event listeners for variant changes
        this.form.addEventListener('change', (event) => {
          if (event.target.name === 'id') {
            this.currentVariant = this.getSelectedVariant();
            this.setAvailability();
          }
        });
       
        // Add listeners for any variant selectors that might change the variant
        const variantSelectors = this.querySelectorAll('[data-variant-selector]');
        if (variantSelectors.length) {
          variantSelectors.forEach(selector => {
            selector.addEventListener('change', () => {
              this.currentVariant = this.getSelectedVariant();
              this.setAvailability();
            });
          });
        }
      }

      getVariantsData() {
        // Check different ways Shopify themes might store variant data
        if (window.productVariants) {
          return window.productVariants;
        } else if (window.product && window.product.variants) {
          return window.product.variants;
        } else {
          // Try to find variant data in a script tag (common practice)
          const variantJson = document.querySelector('#ProductJson-product-template, #ProductJson-template, [data-product-json]');
          if (variantJson) {
            try {
              const productData = JSON.parse(variantJson.textContent);
              return productData.variants || [];
            } catch (e) {
              console.error('Error parsing product JSON:', e);
              return [];
            }
          }
          return [];
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance ? CartPerformance.createStartingMarker('add:wait-for-subscribers') : null;
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                if (CartPerformance && startMarker) CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    if (CartPerformance) {
                      CartPerformance.measure("add:paint-updated-sections", () => {
                        this.cart.renderContents(response);
                      });
                    } else {
                      this.cart.renderContents(response);
                    }
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              if (CartPerformance) {
                CartPerformance.measure("add:paint-updated-sections", () => {
                  this.cart.renderContents(response);
                });
              } else {
                this.cart.renderContents(response);
              }
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            if (CartPerformance) CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      getSelectedVariant() {
        const variantId = parseInt(this.variantIdInput.value);
       
        // First try to get variant from our stored variants data
        if (this.variantsData && this.variantsData.length) {
          return this.variantsData.find(variant => variant.id === variantId);
        }
       
        // Fallback to other methods if variantsData is not available
        if (window.variants) {
          return window.variants.find(variant => variant.id === variantId);
        }
       
        // Try to get from current_variant global that some themes use
        if (window.current_variant && window.current_variant.id === variantId) {
          return window.current_variant;
        }
       
        // Last-resort attempt: try to get price from the DOM
        try {
          // Get price from price element on the page
          const priceContainer = document.querySelector('.price');
          if (priceContainer) {
            const priceElement = priceContainer.querySelector('.price-item--regular, .price__regular .price-item--regular');
            const priceText = priceElement ? priceElement.textContent.trim() : '';
            const priceMatch = priceText.match(/[\d,.]+/);
           
            if (priceMatch) {
              const price = parseFloat(priceMatch[0].replace(/[,.]/g, ''));
              return { id: variantId, price: price };
            }
          }
        } catch (e) {
          console.error('Error finding variant price from DOM:', e);
        }
       
        return null;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings ? window.variantStrings.addToCart : 'Add to cart';
        }
      }

      setAvailability() {
        if (!this.currentVariant) return;
       
        // Find price containers - try different selectors to be more robust
        const priceContainers = document.querySelectorAll('.price, .product__price, .product-price');
       
        if (!priceContainers.length) return;
       
        // Check if price is zero
        const isPriceZero = this.currentVariant.price === 0;
       
        // Apply changes to all price containers (there might be multiple on the page)
        priceContainers.forEach(priceContainer => {
          const regularPrice = priceContainer.querySelector('.price__regular');
          const salePrice = priceContainer.querySelector('.price__sale');
          let contactElement = priceContainer.querySelector('.contact-for-pricing');
         
          if (isPriceZero) {
            // Hide regular price elements
            if (regularPrice) regularPrice.style.display = 'none';
            if (salePrice) salePrice.style.display = 'none';
           
            // Show or create "Contact us for pricing" message
            if (!contactElement) {
              contactElement = document.createElement('span');
              contactElement.className = 'contact-for-pricing';
              contactElement.innerHTML = '<a href="/pages/contact">Contact us for pricing</a>';
              priceContainer.appendChild(contactElement);
            } else {
              contactElement.style.display = 'block';
            }
          } else {
            // Show regular price elements
            if (regularPrice) regularPrice.style.display = '';
            if (salePrice) salePrice.style.display = '';
           
            // Hide "Contact us for pricing" message
            if (contactElement) contactElement.style.display = 'none';
          }
        });
       
        // Also update the Add to Cart button for zero-priced variants
        if (isPriceZero) {
          this.submitButton.style.display = 'none';
        } else {
          this.submitButton.style.display = '';
        }
      }
    }
  );
}